import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { fmtHours } from "@/lib/utils"
import { Head, usePage } from "@inertiajs/react"
import { IconCalendar, IconCalendarMonth, IconClock } from "@tabler/icons-react"
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

interface RecentEntry {
  id: number
  user_name: string
  project_name: string
  customer_name: string
  description: string
  entry_date: string
  hours: number | null
  billable: boolean
  running: boolean
}

interface TeamStat {
  id: number
  name: string
  avatar_url: string | null
  monthly_hours: number
  weekly_hours: number
}

interface Props {
  [key: string]: unknown
  today_hours: number
  this_week_hours: number
  this_month_hours: number
  daily_hours: Record<string, number>
  by_project: Record<string, number>
  recent_entries: RecentEntry[]
  team_stats: TeamStat[]
  is_admin: boolean
}

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"]

function formatDate(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
}

function shortDay(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleDateString("en-US", { weekday: "short" })
}

export default function TimeTrackingDashboard() {
  const {
    today_hours,
    this_week_hours,
    this_month_hours,
    daily_hours,
    by_project,
    recent_entries,
    team_stats,
    is_admin,
  } = usePage<Props>().props

  const dailyChartData = Object.entries(daily_hours)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, hours]) => ({
      name: shortDay(date),
      hours,
    }))

  const projectChartData = Object.entries(by_project).map(([name, hours]) => ({
    name,
    value: hours,
  }))

  return (
    <>
      <Head title="Dashboard - Time Tracking" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/time_entries">Time Tracking</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Dashboard</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="text-xl font-bold">Time Tracking Dashboard</h1>

        {/* Summary Cards */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-3 pt-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                <IconClock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Today</p>
                <p className="text-2xl font-bold">{fmtHours(today_hours)}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 pt-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900">
                <IconCalendar className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">This Week</p>
                <p className="text-2xl font-bold">{fmtHours(this_week_hours)}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 pt-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900">
                <IconCalendarMonth className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">This Month</p>
                <p className="text-2xl font-bold">{fmtHours(this_month_hours)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Daily Hours This Week</CardTitle>
            </CardHeader>
            <CardContent>
              {dailyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dailyChartData}>
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted-foreground flex h-[200px] items-center justify-center text-sm">
                  No data this week
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Hours by Project</CardTitle>
            </CardHeader>
            <CardContent>
              {projectChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={projectChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${fmtHours(value)}`}
                    >
                      {projectChartData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted-foreground flex h-[200px] items-center justify-center text-sm">
                  No project data this month
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Team Utilization (Admin only) */}
        {is_admin && team_stats.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Team Utilization</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">This Week</TableHead>
                    <TableHead className="text-right">This Month</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team_stats.map((stat) => (
                    <TableRow key={stat.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar size="sm">
                            {stat.avatar_url && <AvatarImage src={stat.avatar_url} alt={stat.name} />}
                            <AvatarFallback>{stat.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{stat.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtHours(stat.weekly_hours)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtHours(stat.monthly_hours)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Recent Entries */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recent Entries</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  {is_admin && <TableHead>Employee</TableHead>}
                  <TableHead>Project</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent_entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm">{formatDate(entry.entry_date)}</TableCell>
                    {is_admin && <TableCell className="text-sm">{entry.user_name}</TableCell>}
                    <TableCell className="text-sm">{entry.project_name}</TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{entry.description}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {entry.hours ? fmtHours(entry.hours) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {recent_entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={is_admin ? 5 : 4} className="text-muted-foreground text-center">
                      No recent entries
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

TimeTrackingDashboard.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
